import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Typography,
  Box,
  Chip,
  Button,
  Tab,
  Tabs,
  Divider,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Update as UpdateIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
  GroupAdd as GroupAddIcon,
  CheckCircleOutline as ApprovedIcon,
  HighlightOff as RejectedIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { InAppNotification, NotificationType } from '../types/Activity';

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
  desktopMode?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ height: '100%' }}>
    {value === index && children}
  </div>
);

const NotificationCenter: React.FC<NotificationCenterProps> = ({ open, onClose }) => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
    handleNotificationClick,
    refreshNotifications,
    stats
  } = useNotifications();

  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleNotificationItemClick = (notification: InAppNotification) => {
    console.log('🔔 NotificationCenter: Item clicked, starting navigation');
    // First handle the navigation
    handleNotificationClick(notification);
    
    // Close dialog after a longer delay to ensure navigation protection works
    // This is especially important on mobile where navigation might be slower
    setTimeout(() => {
      console.log('🔔 NotificationCenter: Calling onClose after 300ms delay');
      onClose();
    }, 300); // Increased from 100ms to 300ms to ensure flag is still true
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the notification click
    try {
      await deleteNotificationById(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'participation_approved':
        return <ApprovedIcon color="success" />;
      case 'participation_rejected':
        return <RejectedIcon color="error" />;
      case 'activity_updated':
        return <UpdateIcon color="info" />;
      case 'activity_cancelled':
        return <CancelIcon color="error" />;
      case 'organizer_message':
        return <MessageIcon color="primary" />;
      case 'activity_reminder':
        return <ScheduleIcon color="warning" />;
      case 'activity_invitation':
        return <GroupAddIcon color="primary" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'participation_approved':
        return 'success';
      case 'participation_rejected':
        return 'error';
      case 'activity_updated':
        return 'info';
      case 'activity_cancelled':
        return 'error';
      case 'organizer_message':
        return 'primary';
      case 'activity_reminder':
        return 'warning';
      case 'activity_invitation':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return date.toLocaleDateString();
  };

  const getFilteredNotifications = (tabIndex: number): InAppNotification[] => {
    switch (tabIndex) {
      case 0: // All
        return notifications;
      case 1: // Unread
        return notifications.filter(n => !n.read);
      case 2: { // Today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return notifications.filter(n => new Date(n.created_at) >= today);
      }
      default:
        return notifications;
    }
  };

  const filteredNotifications = getFilteredNotifications(selectedTab);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: 600 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsActiveIcon />
            </Badge>
            <Typography variant="h6">Notifications</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" onClick={handleMarkAllAsRead}>
                  <DoneAllIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={refreshNotifications} disabled={loading}>
                <NotificationsIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Stats Summary */}
        {stats && (
          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip 
                label={`${stats.total_notifications} Total`} 
                size="small" 
                variant="outlined"
              />
              <Chip 
                label={`${stats.unread_count} Unread`} 
                size="small" 
                color="error"
                variant={stats.unread_count > 0 ? "filled" : "outlined"}
              />
              <Chip 
                label={`${stats.today_count} Today`} 
                size="small" 
                color="primary"
                variant="outlined"
              />
              <Chip 
                label={`${stats.this_week_count} This Week`} 
                size="small" 
                color="secondary"
                variant="outlined"
              />
            </Box>
          </Box>
        )}

        <Divider />

        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`All (${notifications.length})`} />
          <Tab label={`Unread (${unreadCount})`} />
          <Tab label={`Today (${stats?.today_count || 0})`} />
        </Tabs>

        {/* Notification List */}
        <Box sx={{ height: 'calc(100% - 120px)', overflow: 'auto' }}>
          <TabPanel value={selectedTab} index={selectedTab}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <Typography color="textSecondary">Loading notifications...</Typography>
              </Box>
            ) : filteredNotifications.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No notifications
                </Typography>
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  {selectedTab === 1 ? 
                    "You're all caught up! No unread notifications." :
                    "You'll see your notifications here when you receive them."
                  }
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredNotifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        bgcolor: notification.read ? 'transparent' : 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' },
                        borderLeft: notification.read ? 'none' : '4px solid',
                        borderColor: `${getNotificationColor((notification.type || 'activity_reminder') as NotificationType)}.main`,
                      }}
                      onClick={() => handleNotificationItemClick(notification)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: notification.read ? 'grey.300' : `${getNotificationColor((notification.type || 'activity_reminder') as NotificationType)}.light`,
                          color: notification.read ? 'grey.600' : `${getNotificationColor((notification.type || 'activity_reminder') as NotificationType)}.main`
                        }}>
                          {getNotificationIcon((notification.type || 'activity_reminder') as NotificationType)}
                        </Avatar>
                      </ListItemAvatar>
                      
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={notification.read ? 'normal' : 'bold'}
                              sx={{ flexGrow: 1 }}
                            >
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatTime(notification.created_at)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            {/* Backend only provides message field, no separate body */}
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={(notification.type || 'notification').replace('_', ' ')}
                                size="small"
                                color={getNotificationColor((notification.type || 'activity_reminder') as NotificationType) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                      
                      <Box display="flex" flexDirection="column" gap={1}>
                        {!notification.read && (
                          <Tooltip title="Mark as read">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                            >
                              <CheckCircleIcon color="success" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteNotification(notification.id, e)}
                          >
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    {index < filteredNotifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </TabPanel>
        </Box>

        {/* Action Buttons */}
        {filteredNotifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                size="small"
              >
                Preferences
              </Button>
              <Box display="flex" gap={1}>
                {unreadCount > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<DoneAllIcon />}
                    onClick={handleMarkAllAsRead}
                    size="small"
                  >
                    Mark All Read
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={refreshNotifications}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationCenter; 